import React, { useState } from 'react';
import { HeartIcon, FlagIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import SupportTicketModal from '../SupportTicketModal';

interface ProductCardProps {
  product: {
    id: string;
    name: string;
    category: string;
    distance?: string;
    rating: number;
    reviewCount?: number;
    reviews?: number;
    currentPrice: string;
    originalPrice?: string;
    discount?: string;
    badge?: string;
    image?: string;
    vendorId?: string;
    vendorName?: string;
    categoryId?: string;
  };
  onAddToCart?: (product: any) => void;
  onFavorite?: (productId: string) => void;
  className?: string;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onAddToCart,
  onFavorite,
  className = '',
}) => {
  const { isAuthenticated, openAuthModal } = useAuth();
  const [showFlagModal, setShowFlagModal] = useState(false);

  const handleFlagClick = () => {
    if (!isAuthenticated) {
      openAuthModal('login');
      return;
    }
    setShowFlagModal(true);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span
        key={i}
        className={`star ${i < Math.floor(rating) ? 'filled text-yellow-400' : i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
      >
        ★
      </span>
    ));
  };

  return (
    <div className={`deal-card product-card relative overflow-hidden ${className}`}>
      {/* Badge Container */}
      <div className="badge-container">
        {product.discount && (
          <div className="deal-badge">{product.discount}</div>
        )}
        {product.badge && (
          <div className="product-badge">{product.badge}</div>
        )}
      </div>

      <button
        type="button"
        onClick={handleFlagClick}
        className="flag-btn"
        title="Report this product"
      >
        <FlagIcon className="flag-icon" />
      </button>

      {/* Favorite Button */}
      <button
        onClick={() => onFavorite?.(product.id)}
        className="favorite-btn"
      >
        <HeartIcon className="heart-icon" />
      </button>

      {/* Product Image */}
      <div className="product-image" style={{ height: '200px', overflow: 'hidden' }}>
        {product.image ? (
          <img 
            src={product.image} 
            alt={product.name} 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain',
              objectPosition: 'center',
              padding: '8px'
            }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = `<div class="text-gray-400 text-sm flex items-center justify-center h-full">${product.name}</div>`;
              }
            }}
          />
        ) : (
          <div className="text-gray-400 text-sm flex items-center justify-center h-full p-2 text-center">
            {product.name}
          </div>
        )}
      </div>

      {/* Product Content */}
      <div className="product-content">
        <h3 className="product-name">{product.name}</h3>
        <p className="product-category">{product.category}</p>
        {product.vendorName && (
          <p className="product-vendor">Sold by {product.vendorName}</p>
        )}
        
        <div className="product-rating">
          <div className="stars">
            {renderStars(product.rating)}
          </div>
          <span className="review-count">({product.reviewCount || product.reviews || 0} reviews)</span>
        </div>

        <div className="product-price">
          <span className="current-price">{product.currentPrice}</span>
          {product.originalPrice && (
            <span className="original-price">{product.originalPrice}</span>
          )}
        </div>

        <button
          type="button"
          className="add-to-cart-btn"
          onClick={() => {
            if (!isAuthenticated) {
              openAuthModal('login');
              return;
            }
            onAddToCart?.(product);
          }}
        >
          Add to Cart
        </button>
      </div>

      <SupportTicketModal
        isOpen={showFlagModal}
        onClose={() => setShowFlagModal(false)}
        type="product_flag"
        product={{ id: product.id, name: product.name }}
      />
    </div>
  );
};

export default ProductCard; 